import { apiClient as api } from "./httpClient";

/**
 * Documentos emitidos pelo prontuário (aba Documentos, item 64) e o catálogo de tipos por
 * entidade com corpo padrão (item 122). Tipos ativos são lidos por quem emite (`patients`);
 * administrar a lista é da tela Administrativo → Tipos de documento (`patient-document-types`).
 */
export interface PatientDocumentTypeRow {
  id: string;
  name: string;
  code: string | null;
  /** HTML do editor; nulo = o tipo só classifica */
  defaultContent: string | null;
  order: number;
  active: boolean;
  documentsCount: number;
}

export interface SavePatientDocumentTypePayload {
  id?: string | null;
  name: string;
  defaultContent: string | null;
  order: number;
  active: boolean;
}

export interface PatientDocumentRow {
  id: string;
  /** Nome do tipo no ato da emissão (ou o título, no Personalizado) */
  kind: string;
  documentTypeId: string | null;
  /** yyyy-mm-dd */
  date: string;
  issuer: string;
  /** HTML do editor — é o que sai no papel */
  content: string;
  canEdit: boolean;
}

export interface SavePatientDocumentPayload {
  id?: string | null;
  kind: string;
  documentTypeId: string | null;
  date: string;
  content: string;
}

export const patientDocumentTypesService = {
  async getActive(): Promise<PatientDocumentTypeRow[]> {
    const response = await api.get("/patient-document-types");
    return response.data;
  },
  async getAll(): Promise<PatientDocumentTypeRow[]> {
    const response = await api.get("/patient-document-types/manage");
    return response.data;
  },
  async save(payload: SavePatientDocumentTypePayload): Promise<string> {
    const response = await api.post("/patient-document-types", payload);
    return response.data.id;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/patient-document-types/${id}`);
  },
};

export interface DocumentTemplateVariable {
  key: string;
  group: string;
  label: string;
  example: string;
}

export interface RenderedPatientDocument {
  content: string;
  values: Record<string, string>;
}

export const patientDocumentsService = {
  /** Catálogo das variáveis do texto padrão (RN-537s), servido pela API para não divergir do que ela troca. */
  async getVariables(): Promise<DocumentTemplateVariable[]> {
    const response = await api.get("/patient-documents/variables");
    return response.data;
  },
  /** Troca as variáveis no servidor, respeitando o bloqueio de dados sensíveis do perfil. */
  async render(patientId: string, content: string, date: string): Promise<RenderedPatientDocument> {
    const response = await api.post(`/patient-documents/by-patient/${patientId}/render`, { content, date });
    return response.data;
  },
  async getByPatient(patientId: string): Promise<PatientDocumentRow[]> {
    const response = await api.get(`/patient-documents/by-patient/${patientId}`);
    return response.data;
  },
  async save(patientId: string, payload: SavePatientDocumentPayload): Promise<string> {
    const response = await api.post(`/patient-documents/by-patient/${patientId}`, payload);
    return response.data.id;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/patient-documents/${id}`);
  },
};
